import React, { useMemo, useCallback, useEffect, useState } from "react";
import { useMatches, useLocation, useNavigate } from "react-router-dom";
import { HeaderWithSubnavigation } from "@mlpa/core-ui-core-open_ocean_ui";
import { getDisabledFeatures, type FeatureKey } from "authentication/runtimeConfig";
import { useResetGridFilters } from "components/templates/dataGridTable/serverSide/gridStatePersist";

interface RouteHandle {
  childLinks?: Array<{ To: string; label: string; featureKey?: FeatureKey }>;
  hasSubNavigation?: boolean;
  hideTabs?: boolean;
}

interface RouteMatch {
  pathname: string;
  handle?: RouteHandle;
}

interface MainLayoutTabsProps {
  title: string;
}

const LAST_VISITED_ROUTES_KEY = "mainLayoutLastVisitedRoutes";

// Helper: Get last visited route map from localStorage
const getLastVisitedRoutes = (): Record<string, string> => {
  try {
    const stored = localStorage.getItem(LAST_VISITED_ROUTES_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Helper: Save last visited route for a first-level path
const saveLastVisitedRoute = (firstLevelPath: string, fullPath: string) => {
  try {
    const routes = getLastVisitedRoutes();
    routes[firstLevelPath] = fullPath;
    localStorage.setItem(LAST_VISITED_ROUTES_KEY, JSON.stringify(routes));
  } catch {
    // Ignore localStorage errors
  }
};

const MainLayoutTabsComponent: React.FC<MainLayoutTabsProps> = ({ title }) => {
  const matches = useMatches() as RouteMatch[];
  const location = useLocation();
  const navigate = useNavigate();
  const resetGridFilters = useResetGridFilters();
  const titleStatusLabel = location.pathname.startsWith("/securityCenter/vulnerabilities") ? "Beta" : undefined;

  // Use matches[1] (first-level route) if it has childLinks, otherwise check matches[2] (nested route)
  // matches[0] is MainLayout itself
  const routeWithLinks = (matches[1] as RouteMatch)?.handle?.childLinks?.length
    ? matches[1]
    : (matches[2] as RouteMatch)?.handle?.childLinks?.length
    ? (matches[2] as RouteMatch)
    : matches[1];
  const childLinks = useMemo(() => {
    const links = (routeWithLinks as RouteMatch)?.handle?.childLinks || [];
    const disabledFeatures = getDisabledFeatures();
    if (disabledFeatures.size === 0) return links;
    return links.filter((link) => !link.featureKey || !disabledFeatures.has(link.featureKey));
  }, [routeWithLinks]);
  const routePath = routeWithLinks?.pathname;

  // Local state for active tab (derived from URL)
  const [activeTab, setActiveTab] = useState(0);

  // Sync activeTab with current URL on mount and location change
  useEffect(() => {
    const currentPath = location.pathname;
    let tabIndex = 0; // Default to first tab
    let longestMatch = "";

    childLinks.forEach((link, index) => {
      let fullPath: string;

      // Handle empty or relative base paths
      if (link.To === "" || link.To === ".") {
        fullPath = routePath;
      } else if (link.To.startsWith("/")) {
        fullPath = link.To;
      } else {
        fullPath = `${routePath}/${link.To}`;
      }

      if (
        (currentPath === fullPath || currentPath.startsWith(fullPath + "/")) &&
        fullPath.length > longestMatch.length
      ) {
        tabIndex = index;
        longestMatch = fullPath;
      }
    });

    setActiveTab(tabIndex);

    // Save last visited route for this first-level path
    // Only save if NOT on the base route (tab index 0) to preserve child route memory
    if (routePath && tabIndex > 0) {
      saveLastVisitedRoute(routePath, currentPath);
    }
  }, [location.pathname, childLinks, routePath]);

  // Convert childLinks to TabBarHorizontal format
  const tabs = useMemo(() => {
    return childLinks.map((link) => ({ label: link.label }));
  }, [childLinks]);

  // Handle tab change and navigation
  const handleTabChange = useCallback(
    (index: number) => {
      if (childLinks[index]) {
        setActiveTab(index);
        resetGridFilters();
        const targetPath = childLinks[index].To;
        // Navigate relative to current first-level route
        if (targetPath === "" || targetPath === ".") {
          navigate(routePath);
        } else if (targetPath.startsWith("/")) {
          navigate(targetPath);
        } else {
          navigate(`${routePath}/${targetPath}`);
        }
      }
    },
    [childLinks, setActiveTab, navigate, routePath, resetGridFilters]
  );

  // Handle click on an already-active tab — MUI Tabs.onChange only fires when the index changes,
  // so clicking the active tab again would be silently ignored without this handler.
  // TODO: Replace this DOM-based workaround with a proper `onTabClick` callback in the
  // @mlpa/core-ui-core-open_ocean_ui TabBarHorizontal / HeaderWithSubnavigation components
  // that fires on every tab click regardless of whether the selected index changes.
  const handleTabsAreaClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const tabEl = (e.target as HTMLElement).closest('[role="tab"]');
      if (!tabEl) return;
      const tablist = tabEl.closest('[role="tablist"]');
      if (!tablist) return;
      const allTabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
      const clickedIndex = allTabs.indexOf(tabEl as HTMLElement);
      if (clickedIndex >= 0 && clickedIndex === activeTab) {
        handleTabChange(clickedIndex);
      }
    },
    [activeTab, handleTabChange]
  );

  // Check if any deeper matched route has its own sub-navigation (e.g. HorizontalTabsForChildLinks)
  const hasSubNavigation = matches.slice(2).some((m) => (m as RouteMatch).handle?.hasSubNavigation);

  // Determine if tabs should be hidden based on any matched route's handle.hideTabs
  const shouldHideTabs = useMemo(() => matches.some((m) => (m as RouteMatch).handle?.hideTabs === true), [matches]);

  const borderBottomSx = { borderBottom: "1px solid", borderColor: "grey.300" };
  if (childLinks.length === 0) {
    return (
      <HeaderWithSubnavigation
        title={title}
        statusLabel={titleStatusLabel}
        sx={hasSubNavigation ? undefined : borderBottomSx}
      />
    );
  }
  return (
    <HeaderWithSubnavigation
      title={title}
      statusLabel={titleStatusLabel}
      hideTabs={shouldHideTabs}
      tabBarProps={{
        tabs,
        onChange: handleTabChange,
        value: activeTab,
        onClick: handleTabsAreaClick,
      }}
    />
  );
};

// React.memo optimiert Re-Renders - nur bei tatsächlichen Änderungen der Props
export const MainLayoutTabs = React.memo(MainLayoutTabsComponent);
